import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, Field, inputClass, btnClass, AuthLink } from "./AuthShell";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const routeByRole = (user, token) => {
    login(token, user);
    if (["super_admin", "officer"].includes(user.role)) nav("/admin/dashboard");
    else nav("/app/dashboard");
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.otp_required) {
        setOtpToken(data.otp_token);
        setStep("otp");
        toast.info("We emailed you a 6-digit login code.");
        if (data.dev_otp) { setCode(data.dev_otp); toast.message(`Dev OTP: ${data.dev_otp}`); }
      } else {
        toast.success("Welcome back!");
        routeByRole(data.user, data.token);
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { otp_token: otpToken, code });
      toast.success("Verified!");
      routeByRole(data.user, data.token);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-otp", { otp_token: otpToken });
      toast.success("New code sent.");
      if (data.dev_otp) { setCode(data.dev_otp); toast.message(`Dev OTP: ${data.dev_otp}`); }
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <AuthShell title={step === "password" ? "Sign in" : "Enter your code"}
      subtitle={step === "password" ? "Access your TrustFlow account" : "A one-time code was sent to your email"}
      testid="login-page">
      {step === "password" ? (
        <form onSubmit={submitPassword} className="space-y-4">
          <Field label="Email">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className={inputClass} data-testid="login-email-input" placeholder="you@example.com" />
          </Field>
          <Field label="Password">
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className={inputClass} data-testid="login-password-input" placeholder="••••••••" />
          </Field>
          <button type="submit" disabled={loading} className={btnClass} data-testid="login-submit-button">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
          </button>
          <div className="flex justify-between text-sm">
            <AuthLink to="/forgot-password">Forgot password?</AuthLink>
            <AuthLink to="/signup">Create account</AuthLink>
          </div>
          <div className="text-center text-xs text-slate-400 pt-2">
            <AuthLink to="/admin-recovery">Super Admin account recovery</AuthLink>
          </div>
        </form>
      ) : (
        <form onSubmit={submitOtp} className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 rounded-xl p-3">
            <KeyRound className="w-4 h-4" /> Staff logins require email verification each time.
          </div>
          <Field label="6-digit code">
            <input inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value)}
              className={`${inputClass} text-center text-2xl font-mono tracking-[0.4em]`} data-testid="login-otp-input" placeholder="000000" />
          </Field>
          <button type="submit" disabled={loading} className={btnClass} data-testid="login-otp-submit-button">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Verify & sign in
          </button>
          <div className="flex justify-between text-sm">
            <button type="button" onClick={() => setStep("password")} className="text-slate-500 hover:underline">Back</button>
            <button type="button" onClick={resend} className="text-blue-600 font-medium hover:underline" data-testid="login-resend-otp-button">Resend code</button>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
